import { ReactElement } from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import { EAnalyticsState } from 'constants/navigation';
import AnalyticsABTestTab from 'views/Analytics/AnalyticsABTestTab/AnalyticsABTestTab';
import AnalyticsApiPerformanceTab from 'views/Analytics/AnalyticsApiPerformanceTab/AnalyticsApiPerformanceTab';
import AnalyticsBusinessTab from 'views/Analytics/AnalyticsBusinessTab';
import AnalyticsFinanceTab from 'views/Analytics/AnalyticsFinanceTab';
import AnalyticsView from 'views/Analytics/AnalyticsView';
import { useAnalyticsTabsAccess } from 'views/Analytics/useAnalyticsTabsAccess';
import CouponsView from 'views/Coupons/CouponsView';
import PackageExplorerView from 'views/PackageExplorer/PackageExplorerView';
import PlansView from 'views/Plans/PlansView';
import SegmentsView from 'views/Segments/SegmentsView';
import TestAndReleaseView from 'views/TestAndRelease/TestAndReleaseView';

import { LevelSpecificRoute } from './types';
import { ERouteContext } from './types';
import { createStoreRoutes } from './utils/storeLevelRoutesUtil';

const AnalyticsIndexRedirect = () => {
  const { defaultTab } = useAnalyticsTabsAccess();
  return <Navigate to={defaultTab} replace />;
};

const AnalyticsTabGuard = ({
  tab,
  children
}: {
  tab: EAnalyticsState;
  children: ReactElement;
}) => {
  const { accessibleTabs, defaultTab } = useAnalyticsTabsAccess();

  if (!accessibleTabs.includes(tab)) {
    return <Navigate to={defaultTab} replace />;
  }

  return children;
};

interface IAnalyticsTabRoute {
  path: string;
  tab: EAnalyticsState;
  element: ReactElement;
}

const analyticsTabRoutes: IAnalyticsTabRoute[] = [
  {
    path: 'business',
    tab: EAnalyticsState.BUSINESS,
    element: <AnalyticsBusinessTab />
  },
  {
    path: 'finance',
    tab: EAnalyticsState.FINANCE,
    element: <AnalyticsFinanceTab />
  },
  {
    path: 'ab-test',
    tab: EAnalyticsState.AB_TEST,
    element: <AnalyticsABTestTab />
  },
  {
    path: 'api-performance',
    tab: EAnalyticsState.API_PERFORMANCE,
    element: <AnalyticsApiPerformanceTab />
  }
];

const AnalyticsRoutes = () => (
  <Routes>
    <Route path="/" element={<AnalyticsView />}>
      <Route path="" element={<AnalyticsIndexRedirect />} />
      {analyticsTabRoutes.map(({ path, tab, element }) => (
        <Route
          key={path}
          path={path}
          element={<AnalyticsTabGuard tab={tab}>{element}</AnalyticsTabGuard>}
        />
      ))}
    </Route>
  </Routes>
);

const projectLevelSpecificRoutes: LevelSpecificRoute[] = [
  { path: 'statistics/*', element: <AnalyticsRoutes /> },
  { path: 'segments/*', element: <SegmentsView /> },
  { path: 'package-explorer/*', element: <PackageExplorerView /> },
  { path: 'test-and-release/*', element: <TestAndReleaseView /> },
  { path: 'coupons/*', element: <CouponsView /> }
];

const projectLevelRoutes = (defaultProjectPage: string) =>
  createStoreRoutes(
    'project/:publisherId',
    defaultProjectPage,
    ERouteContext.PROJECT,
    projectLevelSpecificRoutes
  );

export default projectLevelRoutes;
